import { useState } from 'react'
import { Button, Input, Label, Textarea } from '@brent/core-ui/ui'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import type { CreateQuestionInput, QuestionOption } from '@/services/api/questions'

interface QuestionFormProps {
  initialStatement?: string
  initialOptions?: QuestionOption[]
  onSubmit: (input: CreateQuestionInput) => void
  isPending: boolean
  submitLabel?: string
}

export default function QuestionForm({
  initialStatement = '',
  initialOptions,
  onSubmit,
  isPending,
  submitLabel = 'Create Question',
}: QuestionFormProps) {
  const [statement, setStatement] = useState(initialStatement)
  const [options, setOptions] = useState<QuestionOption[]>(
    initialOptions ?? [
      { id: 'a', text: '' },
      { id: 'b', text: '' },
    ],
  )

  const handleAddOption = () => {
    const nextId = String.fromCharCode(
      97 + options.length, // a=97
    )
    setOptions([...options, { id: nextId, text: '' }])
  }

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return
    setOptions(options.filter((_, i) => i !== index))
  }

  const handleOptionChange = (index: number, text: string) => {
    const updated = [...options]
    updated[index] = { ...updated[index], text }
    setOptions(updated)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!statement.trim() || options.some((o) => !o.text.trim())) return
    onSubmit({ statement: statement.trim(), options })
  }

  const isValid = statement.trim().length > 0 && options.every((o) => o.text.trim().length > 0)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="statement">Question</Label>
        <Textarea
          id="statement"
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
          placeholder="What would you like to play in the spring concert?"
          rows={3}
        />
      </div>

      <div className="space-y-3">
        <Label>Options</Label>
        {options.map((option, index) => (
          <div key={option.id} className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground w-6">{option.id})</span>
            <Input
              value={option.text}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              placeholder={`Option ${option.id}`}
            />
            {options.length > 2 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveOption(index)}
                aria-label={`Remove option ${option.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={handleAddOption}>
          <Plus className="mr-2 h-4 w-4" />
          Add Option
        </Button>
      </div>

      <Button type="submit" disabled={!isValid || isPending}>
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
    </form>
  )
}
